import angular from 'angular';

import educationUrl from './education.html';

export const EDUCATION_SCOPE_KEY = '_educationScope';

export default function EducationService(
  $document,
  $rootScope,
  $compile,
  $timeout,
  $sce,
  $window,
  $templateRequest
) {
  'ngInject';

  const service = this;

  const scope = $rootScope.$new(true); // new isolated scope
  const body = angular.element($document[0].body);
  let educationRoot;
  let postponingModalsOpened = 0;

  scope.shown = false;
  service.postponedEducationConfig = null;

  if (__TEST__) {
    service._initRoot = _initRoot;
  }

  Object.defineProperty(service, 'scope', {
    get() {
      return scope;
    },
  });

  service.show = async function show(config) {
    if (postponingModalsOpened) {
      service.postponedEducationConfig = config;
      return;
    }

    if (!educationRoot) {
      await _initRoot();
    }

    if (scope.shown) {
      if (config.name && scope.block?.config?.name === config.name) {
        return;
      }

      service.hide();
      await $timeout(500);
    }

    let element = $document[0].querySelector(config.selector);
    if (element) {
      scope.block = { config };
      element = angular.element(element);

      scope.block.element = element;
      const elementScope = element.data(EDUCATION_SCOPE_KEY);
      if (elementScope && config.compile) {
        scope.block.compiledContent = $compile(config.content)(elementScope);
      } else {
        scope.block.rawContent = $sce.trustAsHtml(config.content);
      }

      scope.block.chevron = config.chevron;
      scope.block.offset = config.offset;
      scope.block.margins = getBlockMargins(config);
      scope.block.width = config.width ?? 380;
      scope.block.buttonLabel = config.buttonLabel;
      scope.block.noFooter = config.noFooter;

      scrollToElement(config);
      calculateBlockPosition();

      $rootScope.educationStep = config;
      $rootScope.educationElement = element;
      scope.shown = true;
    }
  };

  function scrollToElement(config) {
    if (config.scrollToElement && $window.scrollBy) {
      scope.block.elementRect = measureElement(scope.block.element);

      if (scope.elementRect.bottom > $window.innerHeight) {
        $window.scrollBy(0, scope.elementRect.bottom - $window.innerHeight);
      } else if (scope.elementRect.top < 0) {
        $window.scrollBy(0, -scope.elementRect.top);
      }
    }
  }

  function getBlockMargins(config) {
    let marginLeft = 0;
    let marginTop = 0;

    if (config.offset) {
      if ('x' in config.offset) {
        marginLeft = config.offset.x;
      }

      if ('y' in config.offset) {
        marginTop = config.offset.y;
      }
    }

    return [marginLeft, marginTop];
  }

  function measureElement(element) {
    let rect = element[0].getBoundingClientRect();
    rect = {
      // DOMRect is not enumerable
      left: rect.left,
      right: rect.right,
      top: rect.top,
      bottom: rect.bottom,
      width: rect.width,
      height: rect.height,
    };

    if (element.attr('education-parent-width') && element[0].parentElement) {
      const parentRect = element[0].parentElement.getBoundingClientRect();
      rect.width = parentRect.width;
      rect.left = parentRect.left;
      rect.right = parentRect.right;
    }

    return rect;
  }

  function calculateBlockPosition() {
    if (!scope.block) {
      return;
    }

    scope.block.style = {};
    scope.block.style.marginLeft = `${scope.block.margins[0]}px`;
    scope.block.style.marginTop = `${scope.block.margins[1]}px`;
    scope.block.elementRect = measureElement(scope.block.element);

    const processByChevron = {
      'left-top': () => {
        scope.block.style.top = `${scope.block.elementRect.top}px`;
        scope.block.style.left = `${scope.block.elementRect.right + 25}px`;
      },
      'left-bottom': () => {
        scope.block.style.bottom = `${$window.innerHeight - (scope.block.elementRect.top + 40)}px`;
        scope.block.style.left = `${scope.block.elementRect.right + 10}px`;
      },
      'right-top': () => {
        scope.block.style.top = `${scope.block.elementRect.top}px`;
        scope.block.style.right = `${$window.innerWidth - scope.block.elementRect.left + 10}px`;
      },
      'right-bottom': () => {
        scope.block.style.bottom = `${$window.innerHeight - (scope.block.elementRect.top - 25)}px`;
        const left = scope.block.elementRect.left + scope.block.elementRect.width / 2;
        scope.block.style.left = `${left}px`;

        if (left + scope.block.width / 2 > window.innerWidth - 5) {
          const correctionOffset = left + scope.block.width / 2 - (window.innerWidth - 5);
          scope.block.style.marginLeft = `${scope.block.margins[0] - correctionOffset}px`;
          scope.block.chevronLeft = `${correctionOffset}px`;
        }
      },
      'top-center': () => {
        scope.block.style.top = `${scope.block.elementRect.bottom + 25}px`;
        const left = scope.block.elementRect.left + scope.block.elementRect.width / 2;
        scope.block.style.left = `${left}px`;

        if (left < 250) {
          scope.block.style.marginLeft = `${scope.block.margins[0] - 250 - left}px`;
        }
      },
      'top-right': () => {
        scope.block.style.top = `${scope.block.elementRect.bottom + 10}px`;
        scope.block.style.right = `${
          $window.innerWidth - scope.block.elementRect.left - scope.block.elementRect.width
        }px`;
      },
      'top-left': () => {
        scope.block.style.top = `${scope.block.elementRect.bottom + 25}px`;
        scope.block.style.left = `${
          scope.block.elementRect.left + scope.block.elementRect.width / 2 - 16
        }px`;
      },
      'bottom-center': () => {
        scope.block.style.bottom = `${$window.innerHeight - (scope.block.elementRect.top - 25)}px`;
        const left = scope.block.elementRect.left + scope.block.elementRect.width / 2;
        scope.block.style.left = `${left}px`;

        if (left + scope.block.width / 2 > window.innerWidth - 5) {
          const correctionOffset = left + scope.block.width / 2 - (window.innerWidth - 5);
          scope.block.style.marginLeft = `${scope.block.margins[0] - correctionOffset}px`;
          scope.block.chevronLeft = `${correctionOffset}px`;
        }
      },
      'left-center': () => {
        scope.block.style.left = `${scope.block.elementRect.right + 25}px`;
        const top = scope.block.elementRect.top + scope.block.elementRect.height / 2;
        scope.block.style.top = `${top}px`;
      },
    };

    if (scope.block.chevron in processByChevron) {
      processByChevron[scope.block.chevron]();
    } else {
      throw new Error('no chevron defined');
    }
  }

  service.hide = function hide(reason) {
    if (!scope.shown) {
      return;
    }

    $rootScope.$broadcast('education:hide', {
      config: scope.block.config,
      reason,
    });

    $rootScope.educationStep = null;
    $rootScope.educationElement = null;
    scope.shown = false;
  };

  scope.hide = service.hide;

  function _elementRectChanged(blockElementRect) {
    return (
      !scope.elementRect ||
      scope.elementRect.left !== blockElementRect.left ||
      scope.elementRect.top !== blockElementRect.top ||
      scope.elementRect.width !== blockElementRect.width ||
      scope.elementRect.height !== blockElementRect.height
    );
  }

  function reposition() {
    $timeout(reposition, 33, false);
    if (!scope.shown || !scope.block?.element) {
      return;
    }

    if (_elementRectChanged(measureElement(scope.block.element))) {
      calculateBlockPosition();
      if (!$rootScope.$$phase) {
        scope.$digest(); // far more performant and sufficient in this case (than $apply)
      }
    }
  }

  if (!__TEST__) {
    reposition();
  }

  $rootScope.$on('opening-modal', (e, modalConfig) => {
    if (modalConfig.postponeOpenedEducation) {
      postponingModalsOpened++;
    }

    if (modalConfig.postponeOpenedEducation && scope.shown) {
      service.postponedEducationConfig = scope.block?.config;
    }

    service.hide('opening-modal');
  });

  $rootScope.$on('closing-modal', (e, modalConfig) => {
    if (modalConfig.postponeOpenedEducation) {
      postponingModalsOpened--;
    }

    if (service.postponedEducationConfig) {
      service.show(service.postponedEducationConfig);
    }
  });

  $rootScope.$on('schedule-view-changed', () => {
    service.hide('schedule-view-changed');
  });

  async function _initRoot() {
    educationRoot = angular.element(await $templateRequest(educationUrl));

    body.append(educationRoot);
    $compile(educationRoot)(scope);
  }
}
